import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import { ChevronLeft, ChevronRight, RefreshCw, AlertCircle } from 'lucide-react';
import { getSyncLog } from '@/services/integrationsRedesignService';
import type { SyncLogEntry, SyncLogPage } from '@/types/integrations-redesign';

export interface SyncLogSectionHandle {
  refresh: () => void;
}

interface SyncLogSectionProps {
  companyId: number;
}

function StatusBadge({ status }: { status: SyncLogEntry['status'] }) {
  switch (status) {
    case 'completed':
      return <Badge variant="outline" className="bg-success-50 text-success-700 border-success-200 text-[11px]">Completed</Badge>;
    case 'failed':
      return <Badge variant="outline" className="bg-error-50 text-error-700 border-error-200 text-[11px]">Failed</Badge>;
    case 'partial':
      return <Badge variant="outline" className="bg-warning-50 text-warning-700 border-warning-200 text-[11px]">Partial</Badge>;
    case 'started':
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[11px]">In Progress</Badge>;
    default:
      return <Badge variant="outline" className="text-[11px]">{status}</Badge>;
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function RecordCounts({ entry }: { entry: SyncLogEntry }) {
  const parts: string[] = [];
  if (entry.recordsCreated > 0) parts.push(`${entry.recordsCreated} created`);
  if (entry.recordsUpdated > 0) parts.push(`${entry.recordsUpdated} updated`);
  if (entry.recordsFailed > 0) parts.push(`${entry.recordsFailed} failed`);
  if (parts.length === 0) return <span className="text-muted-foreground">—</span>;
  return <span>{parts.join(', ')}</span>;
}

const INTEGRATION_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'finance', label: 'Finance' },
  { value: 'timesheet', label: 'Timesheet' },
  { value: 'expense', label: 'Expense' },
  { value: 'planner', label: 'Planner' },
  { value: 'purchasing', label: 'Purchasing' },
];

const PAGE_SIZE = 20;

const SyncLogSection = forwardRef<SyncLogSectionHandle, SyncLogSectionProps>(
  function SyncLogSection({ companyId }, ref) {
    const [data, setData] = useState<SyncLogPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [page, setPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const fetchData = useCallback(async () => {
      setLoading(true);
      try {
        const result = await getSyncLog(
          companyId,
          filterType === 'all' ? undefined : filterType,
          page,
          PAGE_SIZE,
        );
        setData(result);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    }, [companyId, filterType, page]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData,
    }));

    function toggleExpanded(id: number) {
      setExpandedRows(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }

    const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

    return (
      <div className="space-y-4">
        {/* Header / filter bar */}
        <div className="flex items-center justify-between gap-3">
          <Select value={filterType} onValueChange={v => { setFilterType(v); setPage(1); }}>
            <SelectTrigger className="w-[160px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTEGRATION_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value} className="text-xs">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="ghost" size="sm" className="h-8 px-2" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            No sync log entries found.
          </div>
        ) : (
          <>
            {/* Header row */}
            <div className="grid grid-cols-[1fr_80px_80px_70px_1fr_80px] gap-2 px-3 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider border-b">
              <span>Date / Time</span>
              <span>Type</span>
              <span>Status</span>
              <span>Trigger</span>
              <span>Records</span>
              <span>By</span>
            </div>

            {/* Rows */}
            <div className="divide-y">
              {data.items.map(entry => (
                <Collapsible
                  key={entry.id}
                  open={expandedRows.has(entry.id)}
                  onOpenChange={() => entry.status === 'failed' && entry.errorMessage && toggleExpanded(entry.id)}
                >
                  <CollapsibleTrigger asChild>
                    <div
                      className={`grid grid-cols-[1fr_80px_80px_70px_1fr_80px] gap-2 px-3 py-2 text-xs items-center hover:bg-muted/40 transition-colors ${
                        entry.status === 'failed' && entry.errorMessage ? 'cursor-pointer' : ''
                      }`}
                    >
                      <span className="truncate" title={entry.startedAt}>
                        {formatDateTime(entry.startedAt)}
                      </span>
                      <span className="capitalize truncate">{entry.integrationType}</span>
                      <span><StatusBadge status={entry.status} /></span>
                      <span className="truncate text-muted-foreground">{entry.triggerType}</span>
                      <span className="truncate"><RecordCounts entry={entry} /></span>
                      <span className="truncate text-muted-foreground" title={entry.initiatedBy ?? undefined}>
                        {entry.initiatedBy ?? '—'}
                      </span>
                    </div>
                  </CollapsibleTrigger>
                  {entry.status === 'failed' && entry.errorMessage && (
                    <CollapsibleContent>
                      <div className="px-3 pb-3 pt-1">
                        <div className="flex items-start gap-2 rounded-md bg-error-50 border border-error-200 p-2.5 text-xs text-error-700">
                          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          <span className="break-words">{entry.errorMessage}</span>
                        </div>
                      </div>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-3 pt-2 text-xs text-muted-foreground">
                <span>
                  Page {data.page} of {totalPages} ({data.totalCount} entries)
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

export default SyncLogSection;
